/**
 * AuthContext — React context for the Designer's authentication state.
 *
 * Mirrors console/src/context/AuthContext.js so the trust model is identical
 * across Console and Designer. Differences:
 *   - Calls dbam's /dbam/v1/me directly (not Console's /api/console/me
 *     proxy), since dbam now verifies Hanko JWTs natively.
 *   - No invite/passkey flows (Designer is for already-onboarded authors;
 *     onboarding lives in Console).
 *
 * The Hanko session cookie is set by Hanko itself, scoped to .samsurveyor.com.
 * Both Designer and dbam live under that parent domain, so the cookie rides
 * along on the credentialed fetch to /dbam/v1/me without any extra plumbing.
 *
 * Usage:
 *   import { AuthProvider, useAuth } from './context/AuthContext';
 *
 *   // In index.js:
 *   <AuthProvider><ProtectedRoute><App /></ProtectedRoute></AuthProvider>
 *
 *   // In any component:
 *   const { user, loading, logout } = useAuth();
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { END_REASON_SIGNOUT } from '@cloudsam/ui';
import hanko from '../hanko-init';
import { getServiceUrl } from '../sysconfig';
import { setOrgContext, setUnauthorizedHandler } from '../utils/cloudApi';
import { idleClock, draftStore } from '../idle-session';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // True once the idle clock has been started for the session this page
    // load is holding. Reset when a check finds nobody signed in, so a
    // sign-in later in the same page load starts the clock again.
    const sessionStartedRef = useRef(false);

    /**
     * Check if the user has an active Hanko session, then fetch their
     * sam-dbam profile from /dbam/v1/me. The Hanko cookie rides along
     * because both this app and dbam are under *.samsurveyor.com.
     */
    const checkAuth = useCallback(async () => {
        try {
            setError(null);
            // NOTE: do NOT gate on hanko.session.isValid() — that SDK check
            // reads the SDK's own localStorage, which is per-origin and will
            // return false on any subdomain that hasn't seen the SDK init
            // a session (e.g. Designer opened fresh from Console). The
            // authoritative check is whether the cookie rides to /me, so
            // just call /me and let the response decide.
            const dbamBase = await getServiceUrl('sam_dbam');
            if (!dbamBase) {
                throw new Error('sam_dbam URL not found in sysconfig');
            }
            const response = await fetch(`${dbamBase.replace(/\/+$/, '')}/dbam/v1/me`, {
                credentials: 'include',
            });

            if (response.ok) {
                const userData = await response.json();
                const userProfile = userData?.data || userData;
                console.log('[AuthContext] /dbam/v1/me →', {
                    user_id: userProfile?.user_id,
                    email: userProfile?.email,
                    org_id: userProfile?.org_id,
                });
                setUser(userProfile);
                // Start the idle clock for this sign-in.
                //
                // This hangs on the session check, not on the Hanko SDK's
                // onSessionCreated event. Both sign-in screens drive the Flow API
                // and call back from its own `success` handler, so that event is not
                // a reliable signal. Relying on it left the end-of-session cookie in
                // place, and every tab opened afterwards locked itself against a
                // session that had just been proved good.
                //
                // It fires on the change from "no session" to "session", so a fresh
                // page load does it once and a repeat check does not touch the clock.
                if (!sessionStartedRef.current) {
                    sessionStartedRef.current = true;
                    idleClock.startSession();
                }
                // Propagate org context to cloudApi so every dbam call is
                // scoped with X-Org-ID / X-User-ID (same model as Console).
                setOrgContext(userProfile?.org_id, userProfile?.user_id);
                // Two people can share a machine. A recovery draft left by
                // somebody else must never be offered to this person.
                try {
                    draftStore.clearDraftsForOtherUsers(userProfile?.user_id || '');
                } catch (err) {
                    console.warn('[AuthContext] could not clear other accounts drafts');
                }
            } else if (response.status === 401) {
                // Cookie missing/expired — fall through to login redirect
                sessionStartedRef.current = false;
                setUser(null);
                setOrgContext(null, null);
            } else if (response.status === 404) {
                // Authenticated to Hanko but no sam-dbam profile yet.
                // Console handles provisioning; the Designer just refuses to load.
                console.warn('[AuthContext] /me 404 — Hanko user has no sam-dbam profile');
                setUser(null);
            } else {
                console.error('[AuthContext] /me failed:', response.status);
                setUser(null);
            }
        } catch (err) {
            console.error('[AuthContext] checkAuth error:', err);
            setError(err);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();

        // A session can die while the tab stays open. Hanko's own events only
        // fire when its SDK notices; a 401 from any dbam call is the earlier
        // and more reliable signal, so treat it as the session ending.
        setUnauthorizedHandler(() => { setUser(null); setOrgContext(null, null); });

        hanko.onSessionExpired(() => { setUser(null); setOrgContext(null, null); });
        hanko.onUserLoggedOut(() => { setUser(null); setOrgContext(null, null); });
        // Clear the "this session has ended" cookie and restart the idle clock.
        // Without this, somebody who signs back in is locked out again on the
        // next tick by the cookie left from the sign-out before.
        hanko.onSessionCreated(() => { idleClock.startSession(); checkAuth(); });

        return () => setUnauthorizedHandler(null);
    }, [checkAuth]);

    /**
     * Sign out: revoke Hanko session and clear local state.
     * The Designer doesn't expose a logout button today, but the function
     * exists so call sites can use it without conditional checks.
     */
    const logout = useCallback(async () => {
        // Tell the other tabs first, before any network call, so Console,
        // Explorer and Channels lock straight away rather than waiting on
        // Hanko. They cannot be closed from here; they watch a shared cookie.
        idleClock.endSession(END_REASON_SIGNOUT);
        // Report the sign-out first, while the session is still valid. Hanko
        // does not tell CloudSAM that a session ended, so without this the
        // stored session expiry stays in the future and the SuperPanel Monitor
        // keeps listing this person as signed in. Never block the sign-out.
        try {
            const dbamBase = await getServiceUrl('sam_dbam');
            if (dbamBase) {
                await fetch(`${dbamBase.replace(/\/+$/, '')}/dbam/v1/me/signout`, {
                    method: 'POST', credentials: 'include',
                });
            }
        } catch (err) {
            console.warn('[AuthContext] sign-out could not be recorded');
        }
        try {
            await hanko.user.logout();
        } catch (err) {
            console.error('[AuthContext] logout error:', err);
        } finally {
            setUser(null);
            setOrgContext(null, null);
        }
    }, []);

    const refreshUser = useCallback(async () => {
        await checkAuth();
    }, [checkAuth]);

    const value = {
        user,
        loading,
        error,
        logout,
        refreshUser,
        isAuthenticated: !!user?.user_id,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
